import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";

import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";

import Dashboard from "./Pages/Dashboard";
import Settings from "./Pages/Settings";
import PowerScore from "./Pages/PowerScoreStats";
import Assessment from "./Pages/Assessment";

import Courses from "./Pages/Courses";

import AssessmentView from "./Pages/AssessmentView";
import HabitTracker from "./Pages/HabitTracker";
import TaskManagement from "./Pages/TaskManagement";
import GoalsAndTargets from "./Pages/GoalsAndTargets";
import GoalsOverview from "./Pages/GoalsOverview";
import BodyTentChat from "./Pages/BodyTentChat";

import Journal from "./Pages/Journal";
import JournalLibrary from "./Pages/JournalLibrary";
import Chat from "./Pages/Chat";
import Threads from "./Pages/Threads";
import Squads from "./Pages/Squads";
import Test from "./Pages/Test";

// login
import SignIn from "./Pages/Auth/SignIn";
import SignUp from "./Pages/Auth/SignUp";
import SignUp2 from "./Pages/Auth/SignUp2";
import SignUp3 from "./Pages/Auth/SignUp3";
import SignUp4 from "./Pages/Auth/SignUp4";
import SignUp5 from "./Pages/Auth/SignUp5";
import SignUp6 from "./Pages/Auth/SignUp6";
import SignUp7 from "./Pages/Auth/SignUp7";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./css/style.css";
import "./App.css";
import AddNewMessage from "./Pages/AddNewMessage";
import CoursesView from "./Pages/CoursesView";
import OnBoarding1 from "./Pages/OnBoarding/OnBoarding1";
import Onboarding from "./Pages/OnBoarding/OnBoarding2";
import ForgotPassword from "./Pages/Auth/ForgotPassword";
import EmailSent from "./Pages/Auth/EmailSent";
// import { AuthProvider } from "./contexts/AuthContext";
// import { SidebarProvider } from "./contexts/SidebarContext";
import ReflectionJournal from "./Components/DashboardJournal/ReflectionJournal";
import ReflectionLibrary from "./Pages/ReflectionLibrary";
import ModulesView from "./Pages/CourseModuleView";
import Tribe from "./Pages/Tribe";
import AssessmentCompletedView from "./Pages/AssessmentCompletedView";
import Squad from "./Pages/Squad";
import { useEffect, useState } from "react";
import useAuthStore from "./zustand-stores/AuthStore";
import useUserStore from "./zustand-stores/UserStore";
import { useLevelUpDetection } from "./hooks/useLevelUpDetection";
import LevelUpModal from "./Components/LevelUpModal";
import ShareLevelupModal from "./Components/LevelAchievements/ShareLevelupModal";
import { getPostViewTracker } from "./helpers/PostViewTracker";
import { api } from "./actions/api";
import WeeklyWarmap from "./Pages/GoalsAndTargets/WeeklyWarmaps";
import Leaderboards from "./Components/leaderboards/leaderboards";
import LeaderboardInfo from "./Components/leaderboards/LeaderboardInfo";

function App() {
  // const protectedRoutes = [
  //   { path: "/dashboard", element: <Dashboard /> },
  //   { path: "/settings", element: <Settings /> },
  //   { path: "/journal/:emotion", element: <Journal /> },
  //   { path: "/journal-library", element: <JournalLibrary /> },
  // ];

  const navigate = useNavigate();
  const { checkAuthSessionExists, authSessionExists, authStateLoading } = useAuthStore();
  const { getUser } = useUserStore();

  const [cacheHandled, setCacheHandled] = useState(false);
  const [sidebarStateHandled, setSidebarStateHandled] = useState(false);

  useLevelUpDetection();

  useEffect(() => {
    try {
      const sidebarState = localStorage.getItem("sidebarState");
      const sidebar_collapsed_state = localStorage.getItem("sidebar_collapsed");

      // Check for mismatches or missing states
      const isExpandedRaw = sidebarState === JSON.stringify("expanded") || sidebarState === "expanded";
      const isCollapsedRaw = sidebarState === JSON.stringify("collapsed") || sidebarState === "collapsed";

      const isCollapsedBoolean = sidebar_collapsed_state === JSON.stringify("true") || sidebar_collapsed_state === "true";
      const isNotCollapsedBoolean = sidebar_collapsed_state === JSON.stringify("false") || sidebar_collapsed_state === "false";

      if ((isExpandedRaw && isCollapsedBoolean) || (isCollapsedRaw && isNotCollapsedBoolean)) {
        console.log("Fixing sidebar state mismatch...");
        localStorage.setItem("sidebarState", JSON.stringify("expanded"));
        localStorage.setItem("sidebar_collapsed", JSON.stringify("false"));
      }
    } catch (e) {
      console.error("Error syncing sidebar state", e);
    } finally {
      setSidebarStateHandled(true);
    }
  }, []);

  useEffect(() => {
    const isCacheCleared = localStorage.getItem("isCacheCleared");

    if (isCacheCleared === null || isCacheCleared === "false") {
      console.log("Clearing all cache and storage...");

      localStorage.clear();
      sessionStorage.clear();

      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0];
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;";
      });

      // ✅ Set flag
      localStorage.setItem("isCacheCleared", "true");

      navigate("/login", { replace: true });

      setCacheHandled(true);
    } else {
      setCacheHandled(true);
    }
  }, [navigate]);

  useEffect(() => {
    if (cacheHandled && sidebarStateHandled) {
      checkAuthSessionExists();
      getUser();
    }
  }, [cacheHandled]);

  // Flush pending post views before page unload (browser close/tab close)
  useEffect(() => {
    const flushPendingViews = async () => {
      try {
        const tracker = getPostViewTracker();
        const pendingViews = tracker.getBatchQueue();

        if (pendingViews.length > 0) {
          console.log(`Flushing ${pendingViews.length} pending post views before page unload...`);
          await tracker.flushBatch(async (payload) => {
            const response = await api.post("/user/v3/views/batch", payload);
            return response;
          });
        }
      } catch (error) {
        console.error("Failed to flush pending views on page unload:", error);
        // Silently fail - user is leaving anyway
      }
    };

    // Handle visibility change (tab switch, minimize) - more reliable than beforeunload
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Page is being hidden - flush views
        flushPendingViews();
      }
    };

    // Handle page unload (browser close, tab close, navigation)
    const handlePageHide = () => {
      // Try to flush - may not complete but we try
      flushPendingViews();
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  if (!cacheHandled || authStateLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* <AuthProvider> */}
      {/* toastr */}
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Level-up modal - globally available */}
      <LevelUpModal />

      {/* Share level-up modal - globally available */}
      <ShareLevelupModal />

      <Routes>
        <Route element={<PublicRoute />}>
          <Route path="/" element={<OnBoarding1 />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/register" element={<SignUp />} />
          <Route path="/login" element={<OnBoarding1 />} />
        </Route>
        <Route path="/sign-up" element={<SignUp2 />} />
        <Route path="/signup/profile" element={<SignUp3 />} />
        <Route path="/signup/details" element={<SignUp4 />} />
        <Route path="/signup/occupation" element={<SignUp5 />} />

        <Route path="/test" element={<Test />} />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/forgot-password/email-sent" element={<EmailSent />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />

          <Route path="/habit-tracker" element={<HabitTracker />} />
          <Route path="/task-management" element={<TaskManagement />} />
          <Route path="/goals-and-targets" element={<GoalsAndTargets />} />
          <Route path="/goals-overview" element={<GoalsOverview />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/threads" element={<Threads />} />
          <Route path="/squads" element={<Squad />} />
          <Route path="/add-new-message" element={<AddNewMessage />} />
          <Route path="/goals-tent" element={<BodyTentChat />} />
          <Route path="/power-score-stats" element={<PowerScore />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/tribe" element={<Tribe />} />

          <Route path="/journal/:emotion" element={<Journal />} />

          <Route path="/journal-library" element={<JournalLibrary />} />
          <Route path="/reflection-library" element={<ReflectionLibrary />} />

          {/* <Route path="/courses-view/:id" element={<CoursesView />} /> */}

          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:courseId/modules" element={<ModulesView />} />
          <Route path="/modules/:moduleId/chapters" element={<CoursesView />} />

          <Route path="/assessment-view" element={<AssessmentView />} />
          <Route path="/assessment-view/completed" element={<AssessmentCompletedView />} />
          <Route path="/reflection-journal/:reflectionType" element={<ReflectionJournal />} />

          <Route path="/notification" element={<SignUp6 />} />
          <Route path="/get-started" element={<SignUp7 />} />

          <Route path="/weekly-warmap" element={<WeeklyWarmap />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/leaderboards-info" element={<LeaderboardInfo />} />
        </Route>
      </Routes>
      {/* </AuthProvider> */}
    </>
  );
}

export default App;
